import React from 'react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Card } from './card';
import { 
  UserGroupIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CalendarIcon,
  PlusIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

// Empty State Component for helpful guidance
export function EmptyState({ 
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  helpText,
  className = '',
  variant = 'default' // default, minimal, detailed
}) {
  const variants = {
    default: 'py-12',
    minimal: 'py-8',
    detailed: 'py-16'
  };

  return (
    <Card className={cn("text-center", variants[variant], className)}>
      <div className="flex flex-col items-center space-y-4">
        {Icon && (
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
            <Icon className="h-8 w-8 text-muted-foreground" />
          </div>
        )}
        
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{description}</p>
          {helpText && (
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <InformationCircleIcon className="h-4 w-4" />
              <span>{helpText}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {action && (
            <Button onClick={action.onClick} className="flex items-center space-x-2">
              {action.icon && <action.icon className="h-4 w-4" />}
              <span>{action.label}</span>
            </Button>
          )}
          {secondaryAction && (
            <Button variant="outline" onClick={secondaryAction.onClick} className="flex items-center space-x-2">
              {secondaryAction.icon && <secondaryAction.icon className="h-4 w-4" />}
              <span>{secondaryAction.label}</span>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// Predefined empty states for common scenarios
export function EmptyClients({ onCreateClient, onImportClients }) {
  return (
    <EmptyState
      icon={UserGroupIcon}
      title="No clients yet"
      description="Start building your client base by adding your first client or importing existing contacts."
      helpText="Clients are the foundation of your relocation business. Each client represents a family or individual moving to Spain."
      action={{
        label: 'Add First Client',
        icon: PlusIcon,
        onClick: onCreateClient
      }}
      secondaryAction={{
        label: 'Import Clients',
        icon: ArrowRightIcon,
        onClick: onImportClients
      }}
      variant="detailed"
    />
  );
}

export function EmptyTasks({ onCreateTask, onViewTemplates }) {
  return (
    <EmptyState
      icon={ClipboardDocumentListIcon}
      title="No tasks yet"
      description="Create your first task to start managing relocation processes, or browse task templates to get started quickly."
      helpText="Tasks help you track the progress of each relocation case and ensure nothing is missed."
      action={{
        label: 'Create Task',
        icon: PlusIcon,
        onClick: onCreateTask
      }}
      secondaryAction={{
        label: 'Browse Templates',
        icon: ArrowRightIcon,
        onClick: onViewTemplates
      }}
    />
  );
}

export function EmptyAccounts({ onCreateAccount, onViewGuide }) {
  return (
    <EmptyState
      icon={BuildingOfficeIcon}
      title="No accounts yet"
      description="Create your first account to organize clients by company or family group, making it easier to manage related relocations."
      helpText="Accounts help you group related clients together, such as families or corporate relocations."
      action={{
        label: 'Create Account',
        icon: PlusIcon,
        onClick: onCreateAccount
      }}
      secondaryAction={{
        label: 'View Guide',
        icon: ArrowRightIcon,
        onClick: onViewGuide
      }}
    />
  );
}

export function EmptyAnalytics({ onGenerateReport, onViewTutorial }) {
  return (
    <EmptyState
      icon={ChartBarIcon}
      title="No analytics data yet"
      description="Analytics will appear here once you have clients and tasks. Start by adding some data to see insights."
      helpText="Analytics help you understand your business performance and identify areas for improvement."
      action={{
        label: 'Generate Report',
        icon: ChartBarIcon,
        onClick: onGenerateReport
      }}
      secondaryAction={{
        label: 'View Tutorial',
        icon: ArrowRightIcon,
        onClick: onViewTutorial
      }}
    />
  );
}

export function EmptyCalendar({ onCreateEvent, onImportCalendar }) {
  return (
    <EmptyState
      icon={CalendarIcon}
      title="No events scheduled"
      description="Start scheduling appointments, meetings, and important dates to keep your relocation cases on track."
      helpText="Calendar events help you stay organized and ensure timely completion of relocation tasks."
      action={{
        label: 'Schedule Event',
        icon: PlusIcon,
        onClick: onCreateEvent
      }}
      secondaryAction={{
        label: 'Import Calendar',
        icon: ArrowRightIcon,
        onClick: onImportCalendar
      }}
    />
  );
}

// Loading state component
export function LoadingState({ message = 'Loading...', className = '' }) {
  return (
    <Card className={cn("text-center py-12", className)}>
      <div className="flex flex-col items-center space-y-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-muted-foreground">{message}</p>
      </div>
    </Card>
  );
}

// Error state component
export function ErrorState({ 
  title = 'Something went wrong',
  description = 'We encountered an error while loading this content.',
  onRetry,
  className = ''
}) {
  return (
    <Card className={cn("text-center py-12", className)}>
      <div className="flex flex-col items-center space-y-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
        </div>
        
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{description}</p>
        </div>

        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            Try Again
          </Button>
        )}
      </div>
    </Card>
  );
}